// 引入加载函数
import { loadCss, loadModules } from 'esri-loader'

// 读取json文件夹下的配置文件
const folderJsonFiles = require.context('../json', true, /\.json$/) // {'development.json': {…}, 'production.json': {…}}

const jsonConfig = {}

for (const JsonFilePath of folderJsonFiles.keys()) {
  // 解析获取文件名作为key
  const fileName = JsonFilePath.replace(/\.\/(.*)\.json/, '$1')
  // 获取文件内容
  const value = folderJsonFiles(JsonFilePath)

  jsonConfig[fileName] = value // jsonConfig: {'development': {…}, 'production': {…}}
}

// 基本地图所需的模块名列表
const modules = [
  'esri/Map',
  'esri/config',
  'esri/Graphic',
  'esri/views/MapView',
  'esri/geometry/Point',
  'esri/layers/TileLayer',
  'esri/layers/GraphicsLayer',
  'esri/identity/IdentityManager',
  'esri/geometry/SpatialReference'
]

// geoserver模块
const GeoModules = [
  "esri/core/Collection",
  "esri/layers/WMSLayer",
  "esri/layers/WebTileLayer",
  "esri/layers/support/TileInfo",
  "esri/layers/support/WMSSublayer"
]

export default {
  props: {
    zoom: { // 地图缩放层级
      type: [String, Number],
      default: 6
    },
    env: { // 环境配置（注意需要和json配置文件名保持统一）
      type: String,
      default: 'development'
    },
    origin: { // 前端部署地址（gisToken获取时需要提供的地址）
      type: String,
      default: window.location.origin
    },
    geoserver: { // 使用geoserver
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      // 配置相关变量
      modules,
      jsonConfig,
      gisConfig: jsonConfig[this.env], // env需要和实际的json文件同名
      gisToken: '',
      gisConstructor: {},
      SR: {},
      // 基础地图相关变量
      map: null,
      baseMaps: [],
      view: {},
      graphicsLayer: null, // 默认绘图层
      groupGraphicMap: {}, // 组合点位id映射
      graphicsActiveLayer: null, // 高亮绘图层
      mapLayerMap: {},
      // 测量占位变量防止报错
      showCoordinate: false,
      currentMeasureType: '',
      coordinateTarget: {}
    }
  },
  computed: {
    mapRandomId () {
      return `j_gis_map_${parseInt(Math.random() * 100)}_${Date.now()}`
    }
  },
  methods: {
    // 初始化
    async _configMap () {
      // 加载css资源
      loadCss(this.gisConfig.static.css)
      // 合并模块
      this.modules = [
        ...this.modules,
        ...(this.drawModules || []),
        ...(this.measureModules || []),
        ...(this.queryModules || []),
        ...(this.layerModules || [])
      ]
      // 追加geo模块
      this.geoserver && this.modules.push(...GeoModules)
      // 加载js资源
      const modules = await loadModules(this.modules, {
        url: this.gisConfig.static.js
      }).catch(error => {
        throw new Error(error)
      })
      for (let index in this.modules) {
        // 分割模块字符串获取最后的模块名
        let name = this.modules[index].split('/').pop()
        // 生成模块函数映射
        this.gisConstructor[name] = modules[index]
      }
      // 读取地图设置生成实例
      const mapOptions = this.gisConfig.mapOptions
      this.SR = new this.gisConstructor.SpatialReference(mapOptions.spatialReference) // "spatialReference": {"wkid": "4490"}
      if (this.gisConfig.token && this.gisConfig.token.tokenSwitch) {
        // 部分gis服务需要token验证
        this.gisToken = await this.GisMapToken()
        // 设置token
        this.gisConstructor.IdentityManager.registerToken({ server: this.gisConfig.token.tokenServer, token: this.gisToken })
      }
      // 配置字体库地址
      this.gisConstructor.config.fontsUrl = this.gisConfig.static.font
    },
    _loadMapByGeoserver () {
      this.map = new this.gisConstructor.Map()
      const tileInfo = new this.gisConstructor.TileInfo(this.gisConfig.tileInfo)
      // const center = new this.gisConstructor.Point(109.894345933182, 19.0377360783462, this.SR)
      const mapServerConfig = this.gisConfig.baseMapsForGeo || []
      for (const map of mapServerConfig) {
        if (map.type === 'geo') {
          const options = {
            tileInfo,
            id: map.label,
            value: map.label,
            visible: map.visible,
            urlTemplate: map.url,
            spatialReference: this.SR
          }
          const layer = new this.gisConstructor.WebTileLayer(options)
          // 渲染基础地图列表
          this.baseMaps.push(options)
          this.map.layers.add(layer)
        }
      }
      this.view = new this.gisConstructor.MapView({
        // center,
        map: this.map,
        zoom: this.zoom,
        spatialReference: this.SR,
        container: this.mapRandomId
      })
    },
    // 载入基础地图
    _loadMap () {
      this.map = new this.gisConstructor.Map()
      const mapServerConfig = this.gisConfig.baseMaps || []
      for (const map of mapServerConfig) {
        if (map.type === 'tiled') {
          const options = {
            id: map.label,
            url: map.url,
            value: map.label,
            visible: map.visible,
            spatialReference: this.SR
          }
          const layer = new this.gisConstructor.TileLayer(options)
          // 渲染基础地图列表
          this.baseMaps.push(options)
          this.map.add(layer)
        }
      }
      this.view = new this.gisConstructor.MapView({
        map: this.map,
        container: this.mapRandomId,
        zoom: this.zoom
      })
    },
    // 绑定交互事件
    _bindMapEvent () {
      // 基础地图左键点击事件（包含图层点击）
      this.view.on('click', (event) => {
        this.GisMapClick(event)
        this._bindLayerEvent(event)
      })
      this.view.on('drag', (event) => {
        this.GisMapDrag(event)
      })
      this.view.on('blur', (event) => {
        this.GisMapBlur(event)
      })
      this.view.on('double-click', (event) => {
        this.GisMapDoubleClick(event)
      })
      this.view.on('focus', (event) => {
        this.GisMapFocusClick(event)
      })
      this.view.on('mouse-wheel', (event) => {
        this.GisMapMouseWheel(event)
      })
    },
    // 绑定图层点击事件 （可获取自定义存储的点位信息）
    _bindLayerEvent (event) {
      // 根据基础地图点击查询图层传递
      this.view.hitTest(event).then(this.GisMapLayerClick)
    },
    async GisMapToken () {
      const { data } = await this.$http.get(this.gisConfig.token.tokenApi, {
        params: {
          httpUrl: this.origin // web系统配置的访问gis服务的客户端地址
        }
      })
      if (data.status !== 'yes') return console.warn(`JSON配置文件token节点中的tokenApi${this.gisConfig.token.tokenApi}接口获取gisToken失败`)
      return data.data.token
    },
    createGrahics () {
      this.graphicsLayer = new this.gisConstructor.GraphicsLayer({ title: '绘制层', graphics: [] })
      this.graphicsActiveLayer = new this.gisConstructor.GraphicsLayer({ title: '高亮绘制层', graphics: [] })
      this.map.add(this.graphicsLayer)
      this.map.add(this.graphicsActiveLayer)
    },
    async createMap () {
      await this._configMap()
      if (this.geoserver) this._loadMapByGeoserver() // geoserver模式
      else this._loadMap() // gis模式
      this._bindMapEvent()
      this.createGrahics() // 创建默认制图层（可根据场景绘制多个）
      if (this.geoserver) {
        this.createGeoserverOperationLayer && this.createGeoserverOperationLayer() // geo网格、专题图层添加
      } else {
        this.createOperationLayer && this.createOperationLayer() // gis网格、专题图层添加
      }
      this.createResourceDraw && this.createResourceDraw() // 资源绘画功能
      this.createQueryTask && this.createQueryTask() // 地图查询任务
    },
    // 建立地图层映射
    buildMapLayer () {
      for (const map of this.baseMaps) {
        this.mapLayerMap[map.id] = this.map.findLayerById(map.id)
      }
    },
    // 根据id设置图层可见,隐藏其他图层
    showLayerById (id) {
      if (this.mapLayerMap[id].visible) return
      for (const mapId in this.mapLayerMap) {
        if (mapId === id) {
          this.mapLayerMap[mapId].visible = true
        } else {
          this.mapLayerMap[mapId].visible = false
        }
      }
    }
  }
}
